/**
 * Org-scoped Projects API — typed wrappers over /api/orgs/:orgId/projects/*
 * (Milestone 2.4, spec §3.6).
 *
 * IMPORTANT: this module is named `projects-v2` to avoid colliding with
 * the legacy `projects.ts` (workspace-scoped /api/kanban/projects/*).
 * The v2 surface is org-scoped, exposes `displayName`, `instructions`,
 * `icon` and a soft-delete via `archivedAt`.
 *
 * Mirrors apps/chaos-gateway/src/projects-boards.ts.
 */
import type {
  CreateOrgScopedProjectInput,
  OrgScopedProject,
  ReorderOrgScopedProjectsInput,
  UpdateOrgScopedProjectInput,
} from '@chaos/types/projects-boards';

import { apiJson } from './client';

interface ProjectsListResponse {
  projects: OrgScopedProject[];
}
interface ProjectResponse {
  project: OrgScopedProject;
}

export const projectsV2Api = {
  list(orgId: string, includeArchived = false): Promise<OrgScopedProject[]> {
    const qs = includeArchived ? '?archived=true' : '';
    return apiJson<ProjectsListResponse>(`/orgs/${orgId}/projects${qs}`).then((r) => r.projects);
  },
  get(orgId: string, projectId: string): Promise<OrgScopedProject> {
    return apiJson<ProjectResponse>(`/orgs/${orgId}/projects/${projectId}`).then(
      (r) => r.project,
    );
  },
  create(orgId: string, input: CreateOrgScopedProjectInput): Promise<OrgScopedProject> {
    return apiJson<ProjectResponse>(`/orgs/${orgId}/projects`, {
      method: 'POST',
      json: input,
    }).then((r) => r.project);
  },
  update(
    orgId: string,
    projectId: string,
    input: UpdateOrgScopedProjectInput,
  ): Promise<OrgScopedProject> {
    return apiJson<ProjectResponse>(`/orgs/${orgId}/projects/${projectId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.project);
  },
  archive(orgId: string, projectId: string): Promise<OrgScopedProject> {
    return apiJson<ProjectResponse>(`/orgs/${orgId}/projects/${projectId}`, {
      method: 'DELETE',
    }).then((r) => r.project);
  },
  // Unified Trash (TRASH_ENABLED) — un-archive a project (404 when the gateway
  // flag is OFF).
  restore(orgId: string, projectId: string): Promise<{ ok: true; id: string }> {
    return apiJson<{ ok: true; id: string }>(`/orgs/${orgId}/projects/${projectId}/restore`, {
      method: 'POST',
    });
  },
  // Unified Trash (TRASH_ENABLED) — PERMANENTLY delete an archived project
  // (gateway tears down any contained session containers first). Founder/admin.
  purge(
    orgId: string,
    projectId: string,
  ): Promise<{ ok: true; id: string; containersRemoved: number }> {
    return apiJson<{ ok: true; id: string; containersRemoved: number }>(
      `/orgs/${orgId}/projects/${projectId}/purge`,
      { method: 'DELETE' },
    );
  },
  // Settings V2 (0064) — reorder projects within a company (1-based positions).
  reorder(orgId: string, input: ReorderOrgScopedProjectsInput): Promise<OrgScopedProject[]> {
    return apiJson<ProjectsListResponse>(`/orgs/${orgId}/projects/reorder`, {
      method: 'POST',
      json: input,
    }).then((r) => r.projects);
  },
};

export const projectsV2Keys = {
  all: ['projects-v2'] as const,
  list: (orgId: string, includeArchived = false) =>
    [...projectsV2Keys.all, 'list', orgId, includeArchived] as const,
  detail: (orgId: string, projectId: string) =>
    [...projectsV2Keys.all, 'detail', orgId, projectId] as const,
};
